/**
 * Whether a Store API amount (a decimal string such as "12.50") is above zero.
 *
 * Spree 6 types money as `string | null`: a channel that hides prices sends
 * `null`. That, and a blank amount, count as not positive.
 *
 * Deliberately not the SDK's `compareMoney`: this runs in client components,
 * and `@spree/sdk` doesn't declare itself side-effect free, so importing a
 * value from it could pull the whole client into the browser bundle.
 */
export function isPositiveMoney(value: string | null | undefined): boolean {
  if (value == null || value === "") return false;
  return Number.parseFloat(value) > 0;
}

const DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * Formats a Store API decimal amount ("1234.50") in its currency: "£1,234.50".
 *
 * Spree 6.1 drops the `display_*` strings, so the storefront formats amounts
 * itself (#263). This gives the SDK's `formatMoney` output for the store's
 * currencies (a test holds the two equal) without importing `@spree/sdk` into
 * client bundles (see isPositiveMoney). The string goes to Intl as is, so the
 * amount never passes through a JavaScript number. A missing or malformed
 * amount, or a currency Intl rejects, formats as "". A locale Intl rejects
 * falls back to the reader's own.
 */
export function formatMoney(
  amount: string | null | undefined,
  currency: string | null | undefined,
  locale?: string,
): string {
  if (typeof amount !== "string" || !DECIMAL.test(amount) || !currency) {
    return "";
  }
  for (const tryLocale of locale ? [locale, undefined] : [undefined]) {
    try {
      const format = new Intl.NumberFormat(tryLocale, {
        style: "currency",
        currency,
      });
      const places = format.resolvedOptions().maximumFractionDigits ?? 2;
      // A negative amount that rounds to zero shows as £0.00, not -£0.00.
      const shown =
        amount.startsWith("-") && Math.abs(Number(amount)) < 0.5 * 10 ** -places
          ? amount.slice(1)
          : amount;
      // ES2023 Intl reads a numeric string exactly; the typings predate it.
      return format.format(shown as unknown as number);
    } catch (error) {
      if (!(error instanceof RangeError)) throw error;
    }
  }
  return "";
}
